import { clsx } from 'clsx';
import { useId, useState } from 'react';
import * as styles from './slider.css';

type SliderProps = {
  min: number;
  max: number;
  step?: number;
  value: number;
  onChange: (value: number) => void;
  ariaLabel: string;
  // Spoken value for assistive tech (e.g. the bracket label rather than "3").
  ariaValueText?: string;
};

// Discrete snapping slider. A transparent native range input owns all
// interaction (click-to-position, drag, keyboard) and accessibility; the
// styled track/fill/thumb/stops render the appearance and always reflect a
// snapped step value.
export const Slider = ({
  min,
  max,
  step = 1,
  value,
  onChange,
  ariaLabel,
  ariaValueText,
}: SliderProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const labelId = useId();

  const clamped = Math.min(max, Math.max(min, value));
  const percent = max > min ? ((clamped - min) / (max - min)) * 100 : 0;

  const stopCount = Math.floor((max - min) / step) + 1;
  const stops = Array.from({ length: stopCount }, (_, i) => ({
    value: min + i * step,
    percent: max > min ? ((i * step) / (max - min)) * 100 : 0,
  }));

  return (
    <div className={styles.wrapper}>
      <div className={styles.range} aria-hidden>
        <div className={styles.track}>
          <div className={styles.fill} style={{ width: `${percent}%` }} />
        </div>

        {stops.map((stop) => (
          <span
            key={stop.value}
            className={clsx(
              styles.stop,
              stop.value <= clamped && styles.stopActive,
            )}
            style={{ left: `${stop.percent}%` }}
          />
        ))}

        <div
          className={clsx(styles.thumb, isFocused && styles.thumbFocused)}
          style={{ left: `${percent}%` }}
        />
      </div>

      <span id={labelId} className={styles.srOnly}>
        {ariaLabel}
      </span>
      <input
        type="range"
        className={styles.input}
        min={min}
        max={max}
        step={step}
        value={clamped}
        aria-labelledby={labelId}
        aria-valuetext={ariaValueText}
        onChange={(event) => onChange(Number(event.target.value))}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
    </div>
  );
};
